import React, { useEffect, useState } from "react";
import styles from "./time-picker.module.css";
import { Loader } from "react-feather";

const MOBILE_BREAKPOINT = "(max-width: 800px)";
const MiN_HEIGHT = 48;

interface TimePickerProps {
    labelText?: string;
    availableTimeSlots?: string[];
    onSelectTime?: (time: string | undefined) => void;
    className?: string;
    maxHeight: number;
}

const TimePicker: React.FunctionComponent<TimePickerProps> = ({
    labelText,
    availableTimeSlots,
    onSelectTime,
    className,
    maxHeight,
}: TimePickerProps) => {
    const [selectedTime, setSelectedTime] = useState<string>();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
        const updateIsMobile = () => setIsMobile(mediaQuery.matches);

        updateIsMobile();
        mediaQuery.addEventListener("change", updateIsMobile);

        return () => {
            mediaQuery.removeEventListener("change", updateIsMobile);
        };
    }, []);

    useEffect(() => {
        setSelectedTime(undefined);
    }, [availableTimeSlots]);

    useEffect(() => {
        if (onSelectTime) {
            onSelectTime(selectedTime);
        }
    }, [selectedTime, onSelectTime]);

    const loading = !availableTimeSlots;
    const disabled = availableTimeSlots && availableTimeSlots.length === 0;
    const active =
        availableTimeSlots && availableTimeSlots.length > 0 && !selectedTime;
    const isCollapsed = !isMobile && Boolean(selectedTime) && !active;

    const onPickerClick = () => {
        setSelectedTime(undefined);
    };

    const onTimeClick = (time: string) => {
        if (isMobile) {
            setSelectedTime(time === selectedTime ? undefined : time);
            return;
        }

        if (selectedTime === time) {
            setSelectedTime(undefined);
            return;
        }

        setSelectedTime(time);
    };

    let rootClass = `${styles.holder}`;
    if (className) rootClass += ` ${className}`;
    if (isMobile) rootClass += ` ${styles.isMobile}`;
    if (active) rootClass += ` ${styles.isActive}`;
    if (isCollapsed) rootClass += ` ${styles.isCollapsed}`;
    if (disabled) rootClass += ` ${styles.disabled}`;
    if (loading) rootClass += ` ${styles.loading}`;

    return (
        <div
            className={rootClass}
            style={{ maxHeight: active || isMobile ? maxHeight : MiN_HEIGHT }}
        >
            <div className={styles.selectedTime} onClick={onPickerClick}>
                {selectedTime ? (
                    selectedTime
                ) : !loading ? (
                    labelText
                ) : (
                    <Loader className="spinorama" />
                )}
            </div>

            {!loading && !disabled && (
                <div className={styles.list}>
                    {(availableTimeSlots || []).map((time) => {
                        const isSlotSelected = selectedTime === time;
                        const isSlotCollapsed =
                            isCollapsed && selectedTime !== time;

                        return (
                            <div
                                key={time}
                                data-time={time}
                                className={`${styles.time} ${
                                    isSlotSelected ? styles.selected : ""
                                } ${
                                    isSlotCollapsed ? styles.slotCollapsed : ""
                                }`}
                                onClick={() => onTimeClick(time)}
                                aria-hidden={isSlotCollapsed}
                                aria-pressed={isSlotSelected}
                            >
                                {time.replace(":", "h")}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TimePicker;
